import React from 'react';
import { useNode } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select, 
  MenuItem, 
  Box,
  Slider,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider
} from "@mui/material";

export const RichTextSettings = () => {
  const { 
    actions: { setProp }, 
    name, 
    text, 
    htmlContent, 
    lexicalState,
    qwantumId,
    mode, 
    alwaysShowEditor, 
    showModeButtons, 
    toolbarConfig,
    fontSize, 
    fontFamily, 
    fontWeight, 
    fontStyle, 
    textAlign, 
    color,
    width,
    height,
    minHeight
  } = useNode((node) => ({
    name: node.data.props.name,
    text: node.data.props.text,
    htmlContent: node.data.props.htmlContent,
    lexicalState: node.data.props.lexicalState,
    qwantumId: node.data.props.qwantumId,
    mode: node.data.props.mode,
    alwaysShowEditor: node.data.props.alwaysShowEditor,
    showModeButtons: node.data.props.showModeButtons,
    toolbarConfig: node.data.props.toolbarConfig || {},
    fontSize: node.data.props.fontSize,
    fontFamily: node.data.props.fontFamily,
    fontWeight: node.data.props.fontWeight,
    fontStyle: node.data.props.fontStyle,
    textAlign: node.data.props.textAlign,
    color: node.data.props.color,
    width: node.data.props.width,
    height: node.data.props.height,
    minHeight: node.data.props.minHeight
  }));

  const updateToolbarConfig = (key, value) => {
    setProp(props => {
      props.toolbarConfig = {
        ...props.toolbarConfig,
        [key]: value
      };
    });
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Qwantum ID (Database Key)</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value || null)}
          size="small"
          placeholder="Database persistence key"
        />
        <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
          When set, content will be persisted to database instead of Craft.js
        </Box>
      </FormControl>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Editor Settings</Typography>
      <Divider sx={{ mb: 2 }} />

      <FormControlLabel
        control={
          <Checkbox
            checked={alwaysShowEditor || false}
            onChange={(e) => setProp(props => props.alwaysShowEditor = e.target.checked)}
          />
        }
        label="Always Show Editor Interface"
      />
      <Box sx={{ fontSize: '12px', color: '#666', mb: 2 }}>
        When enabled, the rich text editor interface will always be visible without needing to click on the text
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={showModeButtons || false}
            onChange={(e) => setProp(props => props.showModeButtons = e.target.checked)}
          />
        }
        label="Show Question/Answer Toggle Buttons"
      />
      <Box sx={{ fontSize: '12px', color: '#666', mb: 2 }}>
        When enabled, shows toggle buttons to switch between question and answer modes for redacted content
      </Box>

      <FormControl fullWidth margin="normal">
        <FormLabel>Display Mode</FormLabel>
        <Select
          value={mode || 'question'}
          onChange={(e) => setProp(props => props.mode = e.target.value)}
          size="small"
        >
          <MenuItem value="question">Question (show input fields for blanks)</MenuItem>
          <MenuItem value="answer">Answer (show original redacted text)</MenuItem>
        </Select>
        <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
          This controls how redacted text appears in display mode
        </Box>
      </FormControl>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Toolbar Configuration</Typography>
      <Divider sx={{ mb: 2 }} />

      <FormControlLabel
        control={
          <Checkbox
            checked={toolbarConfig.showBasicFormatting !== false}
            onChange={(e) => updateToolbarConfig('showBasicFormatting', e.target.checked)}
          />
        }
        label="Show Basic Formatting (Bold, Italic, Underline)"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={toolbarConfig.showAdvancedFormatting !== false}
            onChange={(e) => updateToolbarConfig('showAdvancedFormatting', e.target.checked)}
          />
        }
        label="Show Advanced Formatting"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={toolbarConfig.showListControls !== false}
            onChange={(e) => updateToolbarConfig('showListControls', e.target.checked)}
          />
        }
        label="Show List Controls"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={toolbarConfig.showAlignmentControls !== false}
            onChange={(e) => updateToolbarConfig('showAlignmentControls', e.target.checked)}
          />
        }
        label="Show Alignment Controls"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={toolbarConfig.showFontControls !== false}
            onChange={(e) => updateToolbarConfig('showFontControls', e.target.checked)}
          />
        }
        label="Show Font Controls"
      />

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Content</Typography>
      <Divider sx={{ mb: 2 }} />

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Content</FormLabel>
        <TextField
          value={text || ''}
          onChange={(e) => setProp(props => props.text = e.target.value)}
          size="small"
          multiline
          rows={2}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Lexical State (Read-only)</FormLabel>
        <TextField
          value={lexicalState || ''}
          size="small"
          multiline
          rows={3}
          disabled
          placeholder="Lexical JSON state will appear here"
        />
      </FormControl>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Dimensions</Typography>
      <Divider sx={{ mb: 2 }} />

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={width || '100%'}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          size="small"
          placeholder="e.g., 100%, 500px, auto"
        />
        <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
          Can use CSS units: px, %, em, rem, vw, etc.
        </Box>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Height</FormLabel>
        <TextField
          value={height || 'auto'}
          onChange={(e) => setProp(props => props.height = e.target.value)}
          size="small"
          placeholder="e.g., 450px, 100%, auto"
        />
        <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
          Use "auto" to grow with content, or specify fixed height (px, %, etc.)
        </Box>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Minimum Height</FormLabel>
        <TextField
          value={minHeight || '60px'}
          onChange={(e) => setProp(props => props.minHeight = e.target.value)}
          size="small"
          placeholder="e.g., 60px, 100px"
        />
        <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
          Minimum height when height is set to "auto"
        </Box>
      </FormControl>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Typography</Typography>
      <Divider sx={{ mb: 2 }} />

      <FormControl size="small" component="fieldset" fullWidth margin="normal">
        <FormLabel component="legend">Font Size: {fontSize}px</FormLabel>
        <Slider
          value={fontSize || 16}
          step={1}
          min={8}
          max={72}
          valueLabelDisplay="auto"
          onChange={(_, value) => {
            setProp(props => props.fontSize = value);
          }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Family</FormLabel>
        <Select
          value={fontFamily || 'Arial'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          size="small"
        >
          <MenuItem value="Arial">Arial</MenuItem>
          <MenuItem value="Helvetica">Helvetica</MenuItem>
          <MenuItem value="Montserrat">Montserrat</MenuItem>
          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
          <MenuItem value="Georgia">Georgia</MenuItem>
          <MenuItem value="Verdana">Verdana</MenuItem>
          <MenuItem value="Courier New">Courier New</MenuItem>
          <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
          <MenuItem value="Impact">Impact</MenuItem>
          <MenuItem value="Trebuchet MS">Trebuchet MS</MenuItem>
          <MenuItem value="Arial Black">Arial Black</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={fontWeight === 'bold'}
              onChange={(e) => setProp(props => props.fontWeight = e.target.checked ? 'bold' : 'normal')}
            />
          }
          label="Bold"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={fontStyle === 'italic'}
              onChange={(e) => setProp(props => props.fontStyle = e.target.checked ? 'italic' : 'normal')}
            />
          }
          label="Italic"
        />
      </Box>

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Alignment</FormLabel>
        <Select
          value={textAlign || 'left'}
          onChange={(e) => setProp(props => props.textAlign = e.target.value)}
          size="small"
        >
          <MenuItem value="left">Left</MenuItem>
          <MenuItem value="center">Center</MenuItem>
          <MenuItem value="right">Right</MenuItem>
          <MenuItem value="justify">Justify</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Text Color</FormLabel>
        <Box sx={{ mt: 1 }}>
          <input
            type="color"
            value={color || '#000000'}
            onChange={(e) => setProp(props => props.color = e.target.value)}
            style={{
              width: '50px',
              height: '30px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          />
          <Box sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
            Current color: {color}
          </Box>
        </Box>
      </FormControl>
    </div>
  );
};